import { useCallback } from "react";
import { GQSResult } from "types/api";
import { Action, State as URState, states, useRequest } from "./useRequest";

type RequestType<TData> = (page: number, ...args: unknown[]) => Promise<TData>;
type OtherArgsType<T> = T extends (page: number, ...args: infer U) => any
  ? U
  : never;

export type State<
  TGQSResult extends GQSResult<any> = GQSResult<unknown>,
  TData = TGQSResult extends GQSResult<infer U> ? U : never
> = URState<TGQSResult> & {
  page: number;
  inFlightPage: number;
  dataList?: TData[];
  hasMore?: boolean;
  loaded: boolean;
  lastSearchValue?: string;
};

export type Reducer<TGQSResult extends GQSResult<any> = GQSResult<unknown>> = (
  state: State<TGQSResult>,
  action: Action<TGQSResult>
) => State<TGQSResult>;

function defaultReducer<TGQSResult extends GQSResult<any> = GQSResult<unknown>>(
  state: State<TGQSResult>,
  action: Action<TGQSResult>
): State<TGQSResult> {
  const actionHandlers = {
    [states.loading](state: State<TGQSResult>, action: Action<TGQSResult>) {
      const requestedPage = (action.args?.[0] as number) ?? 1;
      const searchValue = (action.args?.[1] as string) ?? '';

      return {
        ...state,
        loading: true,
        loaded: false,
        inFlightPage: requestedPage,
        lastSearchValue: searchValue
      };
    },
    [states.success](
      state: State<TGQSResult>,
      { data, args }: Action<TGQSResult>
    ) {
      const newPage = (args?.[0] as number) || 1;
      const searchValue = (args?.[1] as string) ?? '';

      // Ignore outdated responses - if this response is for an old search value
      if (state.lastSearchValue !== searchValue) {
        return state;
      }

      const hasMore = data.totalPage > newPage;

      return {
        ...state,
        loading: false,
        loaded: true,
        data,
        page: newPage,
        args: args.slice(1),
        hasMore,
        dataList: newPage === 1 ? data.data : [...state.dataList, ...data.data],
      }
    },
    [states.error](state: State<TGQSResult>, { error }: Action<TGQSResult>) {
      return { ...state, loading: false, loaded: true, error };
    },
  };
  return actionHandlers[action.type]?.(state, action) || state;
}

const defaultInitialState = {
  loading: false,
  loaded: false,
  data: null,
  error: null,
  page: 1,
  inFlightPage: 1,
  args: undefined,
  dataList: [],
  hasMore: true,
  lastSearchValue: '',
};

type LoadFirstPageType<TRequest extends RequestType<any>> = (
  ...args: OtherArgsType<TRequest>
) => void;
type LoadNextPageType = () => void;

/**
 * Custom hook for infinite scroll functionality with GQS (GraphQL Query Service) results
 * 
 * @template TRequest - The request function type that returns GQSResult
 * @template TGQSResult - The GQS result type, automatically inferred from TRequest
 * 
 * @param _request - The request function that takes (page, ...args) and returns Promise<TGQSResult>
 * @param reducer - Optional custom reducer for state management
 * @param initialState - Optional initial state
 * 
 * @returns [state, loadFirstPage, loadNextPage] - State and pagination functions
 */
function useGQSInfiniteScrollRequest<
  TRequest extends RequestType<GQSResult<any>> = RequestType<GQSResult<any>>,
  TGQSResult extends GQSResult<any> = TRequest extends RequestType<infer U> ? U : never,
>(
  _request: TRequest,
  reducer: Reducer<TGQSResult> = defaultReducer as Reducer<TGQSResult>,
  initialState = defaultInitialState
) {
  const [data, send] = useRequest<TGQSResult>(_request as unknown as RequestType<TGQSResult>, reducer, initialState);

  const loadFirstPage: LoadFirstPageType<TRequest> = useCallback(async (
    ...args
  ) => {
    await send(1, ...args);
  }, [send]);

  const loadNextPage: LoadNextPageType = useCallback(async () => {
    const currentState = data as State<TGQSResult>;
    // Prevent loading next page if currently loading, no data, or no more data
    if (currentState.loading || !currentState.data || currentState.hasMore === false) {
      return;
    }
    await send(currentState.page + 1, ...(currentState.args || []));
  }, [send, data]);

  return [data, loadFirstPage, loadNextPage] as [
    State<TGQSResult>,
    LoadFirstPageType<TRequest>,
    LoadNextPageType
  ];
}
export default useGQSInfiniteScrollRequest;
